<template>
  <div class="master-monitor-page">
    <div class="master-monitor-shell">
      <template v-if="isAuthenticated">
        <MonitorHeader
          :search-query="searchQuery"
          @update:searchQuery="searchQuery = $event"
        />

        <div class="dashboard-layout">
          <div class="dashboard-main">
            <SnitchPanel :search-query="searchQuery" />
            <SecurityPanel :search-query="searchQuery" />
          </div>

          <div class="dashboard-side">
            <ScreenSnitchPanel :search-query="searchQuery" />
          </div>
        </div>
      </template>

      <template v-else>
        <div class="pin-gate">
          <div class="pin-card">
            <div class="pin-card__brand">
              <div class="pin-card__dot"></div>
              <div>
                <h1>Sensoreal Master Monitor</h1>
                <p>Enter PIN to access</p>
              </div>
            </div>

            <form class="pin-form" @submit.prevent="submitPin">
              <label class="pin-label" for="pin-input">PIN code</label>

              <div
                class="pin-blocks"
                :class="{
                  'pin-blocks--shake': pinShake,
                  'pin-blocks--success': pinSuccess,
                }"
              >
                <input
                  v-for="(digit, index) in pinDigits"
                  :key="index"
                  ref="pinInputs"
                  type="password"
                  inputmode="numeric"
                  maxlength="1"
                  class="pin-block"
                  v-model="pinDigits[index]"
                  @input="onDigitInput(index, $event)"
                  @keydown.backspace="onBackspace(index)"
                />
              </div>

              <p v-if="pinError" class="pin-error">{{ pinError }}</p>

              <button type="submit" class="pin-button">
                Unlock
              </button>
            </form>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import MonitorHeader from "@/components/monitor/MonitorHeader.vue";
import ScreenSnitchPanel from "@/components/monitor/ScreenSnitchPanel.vue";
import SnitchPanel from "@/components/monitor/SnitchPanel.vue";
import SecurityPanel from "@/components/monitor/SecurityPanel.vue";

const AUTH_COOKIE_NAME = "snitch_pin_auth";
const AUTH_COOKIE_VALUE = "1";
const AUTH_COOKIE_DAYS = 7;

export default {
  components: {
    MonitorHeader,
    ScreenSnitchPanel,
    SnitchPanel,
    SecurityPanel,
  },

  data() {
    return {
      searchQuery: "",
      pinDigits: ["", "", "", ""],
      pinError: "",
      isAuthenticated: false,
      pinShake: false,
      pinSuccess: false,
    };
  },

  mounted() {
    this.isAuthenticated = this.hasValidAuthCookie();

    if (!this.isAuthenticated) {
      this.$nextTick(() => {
        this.$refs.pinInputs?.[0]?.focus();
      });
    }
  },

  methods: {
    submitPin() {
      const expectedPin = process.env.VUE_APP_SNITCH_PIN;
      const enteredPin = this.pinDigits.join("");

      if (!expectedPin) {
        this.pinError = "PIN is not configured.";
        return;
      }

      if (enteredPin.length < 4) {
        this.pinError = "Enter full PIN.";
        this.triggerShake();
        return;
      }

      if (enteredPin !== expectedPin) {
        this.pinError = "Incorrect PIN.";
        this.triggerShake();
        this.resetPin();
        return;
      }

      this.pinError = "";
      this.pinSuccess = true;

      setTimeout(() => {
        this.setAuthCookie();
        this.isAuthenticated = true;
        this.pinSuccess = false;
      }, 220);
    },

    onDigitInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, "");

      this.$set(this.pinDigits, index, value);

      if (value && index < 3) {
        this.$refs.pinInputs[index + 1].focus();
      }

      if (index === 3 && value) {
        this.submitPin();
      }
    },

    onBackspace(index) {
      if (this.pinDigits[index]) {
        this.$set(this.pinDigits, index, "");
        return;
      }

      if (index > 0) {
        this.$refs.pinInputs[index - 1].focus();
      }
    },

    resetPin() {
      this.pinDigits = ["", "", "", ""];
      this.$nextTick(() => {
        this.$refs.pinInputs[0].focus();
      });
    },

    triggerShake() {
      this.pinShake = false;

      this.$nextTick(() => {
        this.pinShake = true;

        setTimeout(() => {
          this.pinShake = false;
        }, 420);
      });
    },

    hasValidAuthCookie() {
      const cookies = document.cookie ? document.cookie.split("; ") : [];

      return cookies.some(cookie => {
        return cookie === `${AUTH_COOKIE_NAME}=${AUTH_COOKIE_VALUE}`;
      });
    },

    setAuthCookie() {
      const expires = new Date();
      expires.setDate(expires.getDate() + AUTH_COOKIE_DAYS);

      document.cookie = [
        `${AUTH_COOKIE_NAME}=${AUTH_COOKIE_VALUE}`,
        `expires=${expires.toUTCString()}`,
        "path=/",
        "SameSite=Lax",
      ].join("; ");
    },
  },
};
</script>

<style scoped>
.master-monitor-page {
  height: 100vh;
  overflow: hidden;
  background: #071018;
  color: #e7edf3;
}

.master-monitor-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 12px;
  min-height: 0;
}

.dashboard-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(440px, 1fr);
  gap: 12px;
}

.dashboard-main,
.dashboard-side {
  min-height: 0;
}

.dashboard-main {
  display: grid;
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

.dashboard-side {
  min-height: 0;
  display: flex;
}

.dashboard-side > * {
  flex: 1;
  min-height: 0;
}

.pin-gate {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.pin-card {
  width: 100%;
  max-width: 420px;
  padding: 28px;
  border-radius: 22px;
  background: linear-gradient(
    180deg,
    rgba(7, 22, 36, 0.96),
    rgba(4, 13, 23, 0.98)
  );
  border: 1px solid rgba(90, 130, 170, 0.22);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.pin-card__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}

.pin-card__dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #17d9b2;
  box-shadow: 0 0 18px rgba(23, 217, 178, 0.45);
  flex-shrink: 0;
}

.pin-card__brand h1 {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: #f3f7fb;
}

.pin-card__brand p {
  margin: 4px 0 0;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #95a8bb;
}

.pin-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pin-label {
  font-size: 13px;
  font-weight: 600;
  color: #b5c3d1;
}

.pin-blocks {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.pin-block {
  width: 56px;
  height: 64px;
  text-align: center;
  font-size: 22px;
  border-radius: 14px;
  border: 1px solid rgba(90, 130, 170, 0.28);
  background: rgba(15, 27, 42, 0.9);
  color: #f3f7fb;
  outline: none;
  transition: all 0.2s ease;
}

.pin-block:focus {
  border-color: rgba(23, 217, 178, 0.6);
  box-shadow: 0 0 0 3px rgba(23, 217, 178, 0.15);
}

.pin-blocks--shake {
  animation: pinShake 0.42s ease;
}

.pin-blocks--success .pin-block {
  border-color: rgba(23, 217, 178, 0.75);
  box-shadow: 0 0 0 3px rgba(23, 217, 178, 0.12),
    0 0 18px rgba(23, 217, 178, 0.22);
  animation: pinSuccessPulse 0.22s ease;
}

@keyframes pinShake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-8px);
  }
  40% {
    transform: translateX(7px);
  }
  60% {
    transform: translateX(-5px);
  }
  80% {
    transform: translateX(4px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes pinSuccessPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
  }
}

.pin-error {
  margin: 0;
  font-size: 13px;
  color: #ff7f7f;
}

.pin-button {
  margin-top: 4px;
  height: 46px;
  border: 1px solid rgba(23, 217, 178, 0.3);
  border-radius: 14px;
  background: rgba(23, 217, 178, 0.12);
  color: #eafcf7;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.pin-button:hover {
  background: rgba(23, 217, 178, 0.18);
  border-color: rgba(23, 217, 178, 0.45);
}

.pin-button:active {
  transform: translateY(1px);
}

@media (max-width: 1500px) {
  .dashboard-layout {
    grid-template-columns: minmax(0, 1.35fr) minmax(360px, 1fr);
  }
}

@media (max-width: 1200px) {
  .dashboard-layout {
    grid-template-columns: 1fr;
  }

  .dashboard-main {
    grid-template-rows: auto auto;
  }

  .dashboard-side {
    min-height: 0;
  }
}

@media (max-width: 640px) {
  .master-monitor-shell {
    padding: 10px;
  }

  .pin-card {
    padding: 22px;
    border-radius: 18px;
  }

  .pin-card__brand h1 {
    font-size: 21px;
  }
}
</style>
